import React, {
   useState,
   useEffect,
   useRef,
   memo,
   createContext,
   useContext,
   useCallback,
   useMemo,
} from "react";
import { useUser } from "./User";
import { getCurrentCDSToken } from "./Auth";
import AudioPlayerNextChapter from "./AudioPlayerNextChapter";
import { useTheme } from "./ThemeContext"; // Import useTheme
import config from "./config";
import "./AudioPlayer.css";

// Global audio cache to prevent redundant loading
const audioCache = {
   blobs: {},
   progress: {},
};

// Create Audio Context
export const AudioContext = createContext(null);

// ===== GLOBAL AUDIO SERVICE LOGIC =====

// Audio Provider Component to handle global audio state
export const AudioProvider = ({ children }) => {
   const { userId } = useUser();
   const audioRef = useRef(new Audio()); // Single global audio element
   const audio = audioRef.current;

   // Core audio state
   const [currentBook, setCurrentBook] = useState(null);
   const [currentChapter, setCurrentChapter] = useState(1);
   const [playbackState, setPlaybackState] = useState({
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      loaded: false,
      error: null,
   });

   // Additional refs for internal management
   const lastUpdateTimeRef = useRef(0);
   const fetchInProgressRef = useRef(false);
   const saveProgressTimerRef = useRef(null);
   const saveTimeoutRef = useRef(null);
   const blobUrlRef = useRef(null);
   const lastSavedProgressRef = useRef({ time: 0, chapter: 1 });
   const isSeekingRef = useRef(false);
   const seekEndTimeoutRef = useRef(null);

   // Format time in HH:MM:SS format
   const formatTime = useCallback((seconds) => {
      if (!seconds && seconds !== 0) return "00:00:00";
      seconds = Math.max(0, seconds); // Ensure non-negative value

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
         2,
         "0"
      )}:${String(secs).padStart(2, "0")}`;
   }, []);

   // Format remaining time in a more human-readable format
   const formatRemainingTime = useCallback((seconds) => {
      if (!seconds && seconds !== 0) return "0h 0m remaining";
      seconds = Math.max(0, seconds); // Ensure non-negative value

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      return `${hours}h ${minutes}m remaining`;
   }, []);

   // Format short time display (MM:SS)
   const formatShortTime = useCallback((seconds) => {
      if (!seconds && seconds !== 0) return "00:00";
      seconds = Math.max(0, seconds); // Ensure non-negative value

      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);

      return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
         2,
         "0"
      )}`;
   }, []);

   // Generate a unique key for caching audio and progress
   const getAudioCacheKey = useCallback(() => {
      if (!currentBook) return null;
      return `${currentBook.uuid}-${currentChapter}`;
   }, [currentBook, currentChapter]);

   // Update playback state with throttling
   const updatePlaybackState = useCallback((newState) => {
      const now = Date.now();

      // Prevent updates that are too frequent (throttle to 100ms)
      if (now - lastUpdateTimeRef.current < 100) {
         return;
      }

      lastUpdateTimeRef.current = now;

      setPlaybackState((prevState) => ({
         ...prevState,
         ...newState,
      }));
   }, []);

   // Enhanced current audio setter - Define this early as other functions need it
   const updateCurrentAudio = useCallback(
      (audioData) => {
         if (!audioData) {
            setCurrentBook(null);
            setCurrentChapter(1);
            return;
         }

         console.log("Updating current audio:", audioData);

         // If we already have an audiobook playing, check if it's the same one
         if (
            currentBook &&
            audioData.book &&
            audioData.book.uuid === currentBook.uuid
         ) {
            // Same book, possibly different chapter
            if (audioData.chapterNumber !== currentChapter) {
               setCurrentChapter(audioData.chapterNumber);
            }
         } else {
            // New book - keep playing state if possible
            const wasPlaying = playbackState.isPlaying;

            // Set new book
            setCurrentBook(audioData.book);
            setCurrentChapter(audioData.chapterNumber);

            console.log(
               `Set new current audio. Previous playback state: ${
                  wasPlaying ? "playing" : "paused"
               }`
            );
         }
      },
      [currentBook, currentChapter, playbackState.isPlaying]
   );

   // Apply the saved progress to the audio element - MUST BE DEFINED BEFORE fetchAudio uses it
   const applyProgress = useCallback(
      (savedProgress) => {
         if (!savedProgress || !audio) return;

         const setupSavedPosition = () => {
            // Set time directly on the audio element
            audio.currentTime = savedProgress.time;
            // Update the state to match
            setPlaybackState((prev) => ({
               ...prev,
               currentTime: savedProgress.time,
            }));
            console.log(`Restored position: ${formatTime(savedProgress.time)}`);
         };

         // Wait for audio to be loaded before setting position
         if (audio.readyState >= 2) {
            // Audio is loaded enough to seek
            setupSavedPosition();
         } else {
            // Wait for audio to be loaded
            const loadHandler = () => {
               setupSavedPosition();
               audio.removeEventListener("canplay", loadHandler);
            };
            audio.addEventListener("canplay", loadHandler);
         }
      },
      [audio, formatTime]
   );

   // Save progress to the server with debouncing and error handling
   const saveProgress = useCallback(
      async (
         time = playbackState.currentTime,
         immediate = false,
         event = "NA"
      ) => {
         if (!currentBook) {
            console.log("No current book, skipping save");
            return false;
         }

         console.log(
            `Attempting to save progress for book ${currentBook.title} (${
               currentBook.uuid
            }): Chapter ${currentChapter}, Time ${formatTime(
               time
            )}, Immediate: ${immediate}`
         );

         // Clear any existing timeout to prevent multiple rapid saves
         if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
         }

         // Function to perform the actual save operation
         const performSave = async () => {
            // For testing with short play times, lower this threshold to 0.5 seconds
            if (time < 0.5 && currentChapter === 1) {
               console.log("Time too short, skipping save");
               return false;
            }

            // For immediate saves, skip the change threshold check
            // For regular saves, check if there's been enough change to warrant a save
            if (
               !immediate &&
               lastSavedProgressRef.current.chapter === currentChapter &&
               Math.abs(lastSavedProgressRef.current.time - time) < 1
            ) {
               // Reduced from 5 to 1 second
               console.log("Change too small, skipping save");
               return false;
            }

            try {
               const token = localStorage.getItem("token");
               if (!token) {
                  console.log("No token, skipping save");
                  return false;
               }

               const timeInMs = Math.floor(time * 1000); // Convert to milliseconds

               console.log(
                  `${immediate ? "IMMEDIATE" : "REGULAR"} SAVE: Book ${
                     currentBook.title
                  }, Chapter ${currentChapter}, Time ${formatTime(time)}`
               );

               const response = await fetch(
                  `/api/user/current_listening/${currentBook.uuid}`,
                  {
                     method: "POST",
                     headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                     },
                     body: JSON.stringify({
                        current_chapters: currentChapter,
                        current_chapter_time: timeInMs,
                        save_event: event,
                     }),
                  }
               );

               if (!response.ok) {
                  console.error("Error saving progress:", response.status);
                  return false;
               }

               // Cache the progress
               const audioCacheKey = getAudioCacheKey();
               if (audioCacheKey) {
                  audioCache.progress[audioCacheKey] = {
                     chapter: currentChapter,
                     time: time,
                  };
                  console.log(`Updated progress cache for ${audioCacheKey}`);
               }

               // Update the last saved progress reference
               lastSavedProgressRef.current = { time, chapter: currentChapter };
               console.log(
                  `✅ Progress saved successfully: Chapter ${currentChapter}, Time ${formatTime(
                     time
                  )}`
               );

               return true; // Success
            } catch (err) {
               console.error("Error saving progress:", err);
               return false;
            }
         };

         // Either perform save immediately or use the debounce
         if (immediate) {
            return await performSave(); // Return promise for immediate saves
         } else {
            // Use shorter debounce for testing
            saveTimeoutRef.current = setTimeout(performSave, 100); // Reduced from 300ms to 100ms
            return true; // Can't wait for the timeout
         }
      },
      [
         currentBook,
         currentChapter,
         playbackState.currentTime,
         formatTime,
         getAudioCacheKey,
      ]
   );

   const forceSaveProgress = useCallback(
      async (event = "forced") => {
         if (!currentBook || !audio) {
            console.log("⚠️ Force save: No current book or audio element");
            return false;
         }

         const currentTime = audio.currentTime;
         console.log(
            `🔒 FORCE SAVE: Book ${
               currentBook.title
            }, Chapter ${currentChapter}, Time ${formatTime(currentTime)}`
         );

         // Don't skip any checks - always save regardless of thresholds
         try {
            const token = localStorage.getItem("token");
            if (!token) {
               console.log("❌ Force save: No token available");
               return false;
            }

            const timeInMs = Math.floor(currentTime * 1000); // Convert to milliseconds

            // Make a direct API call without any debouncing
            const response = await fetch(
               `/api/user/current_listening/${currentBook.uuid}`,
               {
                  method: "POST",
                  headers: {
                     "Content-Type": "application/json",
                     Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                     current_chapters: currentChapter,
                     current_chapter_time: timeInMs,
                     save_event: event,
                  }),
               }
            );

            if (!response.ok) {
               console.error("❌ Force save failed:", response.status);
               return false;
            }

            // Cache the progress
            const audioCacheKey = getAudioCacheKey();
            if (audioCacheKey) {
               audioCache.progress[audioCacheKey] = {
                  chapter: currentChapter,
                  time: currentTime,
               };
               console.log(`✅ Force save: Updated cache for ${audioCacheKey}`);
            }

            // Update the last saved progress reference
            lastSavedProgressRef.current = {
               time: currentTime,
               chapter: currentChapter,
            };
            console.log(`✅ Force save: Progress saved successfully`);

            return true;
         } catch (err) {
            console.error("❌ Force save error:", err);
            return false;
         }
      },
      [currentBook, currentChapter, audio, formatTime, getAudioCacheKey]
   );

   // Fetch current progress from the server
   const fetchProgress = useCallback(
      async (bookUuid, chapter) => {
         if (!bookUuid) {
            console.log("No book UUID provided to fetchProgress");
            return null;
         }

         console.log(
            `Fetching progress for book ${bookUuid}, chapter ${chapter}`
         );

         // IMPORTANT: This is critical for chapter navigation - add this check
         // If chapter is different than current chapter, always return null to start from beginning
         if (chapter !== currentChapter) {
            console.log(
               `⚠️ Chapter navigation detected: ${currentChapter} → ${chapter}. Starting from beginning.`
            );

            // Clear any saved progress for the target chapter
            if (audioCache && audioCache.progress) {
               const audioCacheKey = `${bookUuid}-${chapter}`;
               delete audioCache.progress[audioCacheKey];
            }

            return null; // Return null to ensure we start from the beginning
         }

         // Rest of the function remains the same...
         // Generate cache key
         const audioCacheKey = `${bookUuid}-${chapter}`;

         // Check if we have cached progress, but only log it
         if (audioCache.progress[audioCacheKey]) {
            console.log(
               "Found cached progress:",
               audioCache.progress[audioCacheKey]
            );

            // IMPORTANT: Let's always fetch from server during testing
            // to make sure we're getting the latest data
            console.log("Ignoring cache during testing, fetching from server");
         }

         // Prevent multiple fetches
         if (fetchInProgressRef.current) {
            console.log("Fetch already in progress, waiting...");
            // Wait a bit and check again
            await new Promise((resolve) => setTimeout(resolve, 200));
            if (fetchInProgressRef.current) {
               console.log("Fetch still in progress, returning null");
               return null;
            }
         }

         fetchInProgressRef.current = true;

         try {
            const token = localStorage.getItem("token");
            if (!token) {
               console.log("No token found for progress fetch");
               fetchInProgressRef.current = false;
               return null;
            }

            console.log(`Fetching progress from server for book ${bookUuid}`);
            const response = await fetch(
               `/api/user/current_listening/${bookUuid}`,
               {
                  method: "GET",
                  headers: {
                     Authorization: `Bearer ${token}`,
                  },
               }
            );

            if (!response.ok) {
               if (response.status !== 404) {
                  // 404 is expected for new books
                  console.error(`Failed to fetch progress: ${response.status}`);
               } else {
                  console.log("No saved progress found (404)");
               }
               fetchInProgressRef.current = false;
               return null;
            }

            const data = await response.json();
            console.log("Fetched progress from server:", data);

            const progress = {
               chapter: data.current_chapters,
               time: data.current_chapter_time / 1000, // Convert from milliseconds to seconds
            };

            // Cache the progress
            audioCache.progress[audioCacheKey] = progress;
            console.log(`Updated progress cache for ${audioCacheKey}`);

            fetchInProgressRef.current = false;
            return progress;
         } catch (err) {
            console.error("Error fetching progress:", err);
            fetchInProgressRef.current = false;
            return null;
         }
      },
      [currentChapter]
   );

   // Fetch audio data with cache handling - NOW applyProgress is defined before this uses it
   const fetchAudio = useCallback(
      async (book, chapter, savedProgress = null) => {
         if (!book) return;

         // Update playback state to indicate loading
         setPlaybackState((prev) => ({
            ...prev,
            loaded: false,
            error: null,
         }));

         try {
            const audioCacheKey = `${book.uuid}-${chapter}`;

            // Check if we should ignore saved progress based on our special flag
            const resetPositionKey = localStorage.getItem(
               "reset_position_for_chapter"
            );
            const shouldResetPosition = resetPositionKey === audioCacheKey;

            if (shouldResetPosition) {
               console.log(
                  `🔄 Ignoring saved progress for ${audioCacheKey} - starting from beginning`
               );
               savedProgress = null; // Override saved progress
            }

            // Check if audio is already cached
            if (audioCache.blobs[audioCacheKey]) {
               console.log("Using cached audio blob");

               // Always revoke previous blob URL before creating a new one
               if (blobUrlRef.current) {
                  URL.revokeObjectURL(blobUrlRef.current);
               }

               // Create new blob URL from cache
               const newBlobUrl = URL.createObjectURL(
                  audioCache.blobs[audioCacheKey]
               );
               blobUrlRef.current = newBlobUrl;

               // Set the audio source
               audio.src = newBlobUrl;

               // Apply saved progress if available and not resetting
               if (savedProgress && !shouldResetPosition) {
                  applyProgress(savedProgress);
               } else if (shouldResetPosition) {
                  // Force position to start
                  console.log("Forcing audio position to beginning");
                  audio.currentTime = 0;
                  setPlaybackState((prev) => ({
                     ...prev,
                     currentTime: 0,
                  }));
               }

               setPlaybackState((prev) => ({
                  ...prev,
                  loaded: true,
               }));

               return;
            }

            const cdsToken = await getCurrentCDSToken();
            const audioUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/chapters/${chapter}?user_id=${userId}`;

            const headers = {
               Accept: "audio/mpeg",
               Origin: config.primaryUrl,
            };

            if (cdsToken) {
               headers["Authorization"] = JSON.stringify(cdsToken);
            }

            console.log("Fetching audio from:", audioUrl);
            const response = await fetch(audioUrl, {
               method: "GET",
               headers,
               credentials: "include",
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load audio: ${response.status}`);
            }

            const blob = await response.blob();

            // Cache the blob
            audioCache.blobs[audioCacheKey] = blob;

            // Always revoke previous blob URL before creating a new one
            if (blobUrlRef.current) {
               URL.revokeObjectURL(blobUrlRef.current);
            }

            // Store the blob URL in a ref for cleanup
            const newBlobUrl = URL.createObjectURL(blob);
            blobUrlRef.current = newBlobUrl;

            // Set the audio source
            audio.src = newBlobUrl;

            // Apply saved progress if available and not resetting
            if (savedProgress && !shouldResetPosition) {
               applyProgress(savedProgress);
            } else if (shouldResetPosition) {
               // Force position to start
               console.log(
                  "Forcing audio position to beginning for new chapter"
               );
               audio.currentTime = 0;
               setPlaybackState((prev) => ({
                  ...prev,
                  currentTime: 0,
               }));
            }

            setPlaybackState((prev) => ({
               ...prev,
               loaded: true,
            }));
         } catch (err) {
            console.error("Error loading audio:", err);
            setPlaybackState((prev) => ({
               ...prev,
               loaded: false,
               error: err.message,
            }));
         }
      },
      [userId, applyProgress, audio]
   );

   // Toggle play/pause state
   const togglePlayPause = useCallback(() => {
      if (!audio || !currentBook) return;

      if (playbackState.isPlaying) {
         audio.pause();
         // Save progress on pause, but only if we've played for at least 2 seconds
         if (audio.currentTime > 2) {
            saveProgress(audio.currentTime, false, "pause");
         }
         setPlaybackState((prev) => ({
            ...prev,
            isPlaying: false,
         }));
      } else {
         const playPromise = audio.play();

         // Handle play promises to avoid "Uncaught (in promise)" errors
         if (playPromise !== undefined) {
            playPromise
               .then(() => {
                  // Playback started successfully
                  setPlaybackState((prev) => ({
                     ...prev,
                     isPlaying: true,
                  }));
               })
               .catch((err) => {
                  console.error("Error playing audio:", err);
                  setPlaybackState((prev) => ({
                     ...prev,
                     isPlaying: false,
                     error: "Could not play audio. Please try again.",
                  })); // Reset state if playback fails
               });
         } else {
            setPlaybackState((prev) => ({
               ...prev,
               isPlaying: true,
            }));
         }
      }
   }, [audio, currentBook, playbackState.isPlaying, saveProgress]);

   // Seek to a specific time
   const seekTo = useCallback(
      (newTime) => {
         if (!audio) return;

         // Update the audio element's current time
         audio.currentTime = newTime;
         setPlaybackState((prev) => ({
            ...prev,
            currentTime: newTime,
         }));

         // Mark that we're in a seeking operation
         isSeekingRef.current = true;

         // Clear any existing timeout
         if (seekEndTimeoutRef.current) {
            clearTimeout(seekEndTimeoutRef.current);
         }

         // Set a timeout to detect when the user has finished seeking
         seekEndTimeoutRef.current = setTimeout(() => {
            // Only save progress after seeking has stopped for 500ms
            if (isSeekingRef.current) {
               saveProgress(audio.currentTime, false, "seek");
               isSeekingRef.current = false;
            }
         }, 500);
      },
      [audio, saveProgress]
   );

   // Skip forward/backward
   const skip = useCallback(
      (seconds) => {
         if (!audio) return;

         const newTime = Math.min(
            Math.max(audio.currentTime + seconds, 0),
            audio.duration || 0
         );
         audio.currentTime = newTime;
         setPlaybackState((prev) => ({
            ...prev,
            currentTime: newTime,
         }));

         // Only save progress for significant skips
         if (Math.abs(seconds) >= 10) {
            saveProgress(newTime, false, "skip");
         }
      },
      [audio, saveProgress]
   );

   // Load and play a specific book and chapter
   const loadBook = useCallback(
      async (newBook, chapter = 1, autoPlay = false, options = {}) => {
         if (!newBook) {
            console.log("No book provided to loadBook");
            return;
         }

         // Extract options with defaults
         const { ignoreSavedProgress = false } = options;

         if (ignoreSavedProgress) {
            console.log(
               `📚 Loading book with FORCED RESET: ${newBook.title}, Chapter: ${chapter}`
            );
         } else {
            console.log(
               `📚 Loading book: ${newBook.title}, Chapter: ${chapter}`
            );
         }

         // IMPORTANT: Create local references to current state before modifying anything
         // This ensures we don't lose references when state updates
         const previousBook = currentBook ? { ...currentBook } : null;
         const currentTime = audio ? audio.currentTime : 0;

         // Always try to save progress of current book before switching
         if (previousBook && audio) {
            // Lowered the threshold to 0.5 seconds for testing
            if (currentTime > 0.5) {
               console.log(
                  `⏱️ Current position before switching: ${formatTime(
                     currentTime
                  )}`
               );
               console.log(
                  `📋 Current book before switching: ${previousBook.title} (${previousBook.uuid})`
               );

               try {
                  // Save progress for the PREVIOUS book using the local reference
                  const token = localStorage.getItem("token");
                  if (token) {
                     const timeInMs = Math.floor(currentTime * 1000); // Convert to milliseconds

                     console.log(
                        `💾 DIRECT SAVE for previous book: ${
                           previousBook.title
                        }, Chapter ${currentChapter}, Time ${formatTime(
                           currentTime
                        )}`
                     );

                     const response = await fetch(
                        `/api/user/current_listening/${previousBook.uuid}`,
                        {
                           method: "POST",
                           headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`,
                           },
                           body: JSON.stringify({
                              current_chapters: currentChapter,
                              current_chapter_time: timeInMs,
                              save_event: "swap",
                           }),
                        }
                     );

                     if (response.ok) {
                        console.log(
                           `✅ Progress saved successfully for previous book`
                        );

                        // Update cache too
                        const audioCacheKey = `${previousBook.uuid}-${currentChapter}`;
                        audioCache.progress[audioCacheKey] = {
                           chapter: currentChapter,
                           time: currentTime,
                        };
                     } else {
                        console.error(
                           "❌ Error saving progress for previous book:",
                           response.status
                        );
                     }
                  }
               } catch (err) {
                  console.error("Error during direct save:", err);
               }
            } else {
               console.log("Current playback time too short, not worth saving");
            }
         } else {
            console.log(
               "No previous book or audio element to save progress for"
            );
         }

         // Stop current audio if playing
         if (audio && audio.src) {
            audio.pause();
            setPlaybackState((prev) => ({
               ...prev,
               isPlaying: false,
            }));
         }

         // Update book and chapter state
         setCurrentBook(newBook);
         setCurrentChapter(chapter);

         // Fetch saved progress ONLY if we're not ignoring it
         let savedProgress = null;
         if (!ignoreSavedProgress) {
            console.log(
               `🔍 Fetching saved progress for book: ${newBook.title} (${newBook.uuid}), chapter: ${chapter}`
            );
            savedProgress = await fetchProgress(newBook.uuid, chapter);

            if (savedProgress) {
               console.log(
                  `📂 Found saved progress: Chapter ${
                     savedProgress.chapter
                  }, Time ${formatTime(savedProgress.time)}`
               );
            } else {
               console.log("No saved progress found, starting from beginning");
            }
         } else {
            console.log(
               "📢 IGNORING SAVED PROGRESS - Starting chapter from beginning"
            );
         }

         // Fetch and load the audio
         await fetchAudio(newBook, chapter, savedProgress);

         // Update context with the new book info
         updateCurrentAudio({
            book: newBook,
            chapterNumber: chapter,
         });

         // If we're ignoring saved progress, explicitly set position to 0
         if (ignoreSavedProgress && audio) {
            console.log("📢 FORCING POSITION TO BEGINNING");
            audio.currentTime = 0;
            setPlaybackState((prev) => ({
               ...prev,
               currentTime: 0,
            }));
         }

         // Autoplay if requested, but handle browser restrictions gracefully
         if (autoPlay) {
            try {
               const playPromise = audio.play();

               // If the browser returns a Promise (modern browsers do)
               if (playPromise !== undefined) {
                  playPromise
                     .then(() => {
                        // Playback started successfully
                        console.log("▶️ Autoplay successful");
                        setPlaybackState((prev) => ({
                           ...prev,
                           isPlaying: true,
                           error: null, // Clear any previous errors
                        }));
                     })
                     .catch((err) => {
                        // Autoplay prevented by the browser
                        console.log(
                           "⚠️ Autoplay prevented by browser:",
                           err.message
                        );
                        setPlaybackState((prev) => ({
                           ...prev,
                           isPlaying: false,
                           // Don't set this as an error, just silently fail autoplay
                           error: null,
                        }));
                     });
               } else {
                  // For older browsers that don't return a Promise
                  setPlaybackState((prev) => ({
                     ...prev,
                     isPlaying: true,
                     error: null,
                  }));
               }
            } catch (err) {
               console.error("Error during autoplay attempt:", err);
               // Don't set as error, just continue without autoplay
               setPlaybackState((prev) => ({
                  ...prev,
                  isPlaying: false,
                  error: null,
               }));
            }
         }
      },
      [
         audio,
         currentBook,
         currentChapter,
         fetchAudio,
         fetchProgress,
         updateCurrentAudio,
         formatTime,
      ]
   );

   const loadChapterFromBeginning = useCallback(
      (book, chapter) => {
         if (!book) return;

         console.log(
            `🔄 Loading chapter ${chapter} from beginning for book: ${book.title}`
         );

         // 1. Stop any current playback
         if (audio) {
            audio.pause();
            setPlaybackState((prev) => ({
               ...prev,
               isPlaying: false,
            }));
         }

         // 2. Update book and chapter state
         setCurrentBook(book);
         setCurrentChapter(chapter);

         // 3. Force beginning position by skipping fetchProgress
         fetchAudio(book, chapter, null);

         // 4. Update context
         updateCurrentAudio({
            book: book,
            chapterNumber: chapter,
         });

         // 5. Play after a short delay to ensure audio is loaded
         setTimeout(() => {
            // Force position to 0 again just to be sure
            if (audio) {
               audio.currentTime = 0;
               setPlaybackState((prev) => ({
                  ...prev,
                  currentTime: 0,
               }));

               // Play
               const playPromise = audio.play();
               if (playPromise !== undefined) {
                  playPromise
                     .then(() => {
                        console.log("▶️ Playing from beginning successful");
                        setPlaybackState((prev) => ({
                           ...prev,
                           isPlaying: true,
                           error: null,
                        }));
                     })
                     .catch((err) => {
                        console.error("Error playing audio:", err);
                        setPlaybackState((prev) => ({
                           ...prev,
                           isPlaying: false,
                           error: "Could not play audio. Please try again.",
                        }));
                     });
               }
            }
         }, 300);
      },
      [audio, fetchAudio, updateCurrentAudio]
   );

   // Start the progress saving interval when playing
   useEffect(() => {
      if (playbackState.isPlaying && currentBook) {
         console.log("Setting up auto-save interval");

         // Start interval - capture the current saveProgress function
         const currentSaveProgress = saveProgress;
         saveProgressTimerRef.current = setInterval(() => {
            console.log("Auto-save interval triggered");
            if (audio && audio.currentTime > 1) {
               currentSaveProgress(audio.currentTime, false, "listen_interval");
            }
         }, 29000); // Save every 29 seconds
      } else {
         // Clear interval if not playing
         if (saveProgressTimerRef.current) {
            clearInterval(saveProgressTimerRef.current);
            saveProgressTimerRef.current = null;
         }
      }

      // Cleanup
      return () => {
         if (saveProgressTimerRef.current) {
            clearInterval(saveProgressTimerRef.current);
            saveProgressTimerRef.current = null;
         }
      };
   }, [playbackState.isPlaying, currentBook, audio]);

   // Handle audio events
   useEffect(() => {
      // Set up audio event listeners
      const handleTimeUpdate = () => {
         // Directly update state without throttling to ensure UI is responsive
         setPlaybackState((prev) => ({
            ...prev,
            currentTime: audio.currentTime,
         }));
      };

      const handleLoadedMetadata = () => {
         setPlaybackState((prev) => ({
            ...prev,
            duration: audio.duration,
         }));
      };

      const handleEnded = () => {
         setPlaybackState((prev) => ({
            ...prev,
            isPlaying: false,
         }));
         // Save progress at the end of playing
         if (currentBook) {
            saveProgress(audio.duration || 0, false, "end");
         }
      };

      const handleError = (e) => {
         console.error("Audio element error:", e);
         setPlaybackState((prev) => ({
            ...prev,
            isPlaying: false,
            error: "Error playing audio. Please try reloading.",
         }));
      };

      // Add event listeners
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      // Cleanup
      return () => {
         // Remove event listeners
         audio.removeEventListener("timeupdate", handleTimeUpdate);
         audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
         audio.removeEventListener("ended", handleEnded);
         audio.removeEventListener("error", handleError);

         // Save final progress
         if (audio.currentTime > 0 && currentBook) {
            saveProgress(audio.currentTime, false, "finalised");
         }

         // Revoke blob URL
         if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
         }
      };
      // Only depend on audio ref which is stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [audio]);

   // Calculate remaining time for the UI
   const remainingTime = playbackState.duration - playbackState.currentTime;

   // Prepare the context value
   const contextValue = useMemo(
      () => ({
         // Book info
         currentBook,
         currentChapter,

         // Playback state
         playbackState,
         remainingTime,

         // Core control methods
         loadBook,
         togglePlayPause,
         seekTo,
         skip,
         updateCurrentAudio,
         updatePlaybackState,
         loadChapterFromBeginning, // Add this new function

         // Add force save function
         forceSaveProgress,

         // Formatting utilities
         formatTime,
         formatRemainingTime,
         formatShortTime,
      }),
      [
         currentBook,
         currentChapter,
         playbackState,
         remainingTime,
         loadBook,
         togglePlayPause,
         seekTo,
         skip,
         updateCurrentAudio,
         updatePlaybackState,
         loadChapterFromBeginning, // Add this
         forceSaveProgress,
         formatTime,
         formatRemainingTime,
         formatShortTime,
      ]
   );

   return (
      <AudioContext.Provider value={contextValue}>
         {children}
      </AudioContext.Provider>
   );
};

// ===== CORE AUDIO PLAYER COMPONENT =====
export const CoreAudioPlayer = memo(
   ({
      book,
      chapterNumber,
      showChapterInfo = true,
      showAutoplayHelper = true,
      compact = false,
      onClose = null,
      containerStyle = {},
   }) => {
      const {
         currentBook,
         currentChapter,
         playbackState,
         remainingTime,
         loadBook,
         togglePlayPause,
         seekTo,
         skip,
         formatTime,
         formatRemainingTime,
         formatShortTime,
         loadChapterFromBeginning,
      } = useContext(AudioContext);

      const { theme } = useTheme(); // Get current theme

      const { isPlaying, currentTime, duration, loaded, error } = playbackState;

      // For controlling the generate button visibility
      const [showGenerateButton, setShowGenerateButton] = useState(false);
      // Store the next chapter number we want to generate
      const [nextChapterToGenerate, setNextChapterToGenerate] = useState(null);

      const [isChapterEnded, setIsChapterEnded] = useState(false);

      const [manualChapterNavigation, setManualChapterNavigation] = useState(false);
         
      // Add window resize listener to adapt UI
      const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
      
      useEffect(() => {
         const handleResize = () => {
            setViewportHeight(window.innerHeight);
         };
         
         window.addEventListener('resize', handleResize);
         return () => window.removeEventListener('resize', handleResize);
      }, []);
      
      // Calculate if we should use compact mode based on viewport height
      const shouldUseCompactMode = viewportHeight < 700 || compact;

      // Define hooks at the top level of the function, before any conditional returns
      const prevChapter = useCallback(() => {
         // Check if there is a book and if we're not already at the first chapter
         if (!currentBook || currentChapter <= 1) return;

         console.log(
            `⚡ Prev chapter button: Loading chapter ${currentChapter - 1}`
         );

         // No additional check needed for previous chapters since they should always be
         // available if we're already on a chapter
         loadBook(currentBook, currentChapter - 1, playbackState.isPlaying, {
            ignoreSavedProgress: true,
         });

         // Hide generate button when navigating
         setShowGenerateButton(false);

         // Hide the chapter ended UI (added from our fixes)
         setIsChapterEnded(false);
      }, [currentBook, currentChapter, loadBook, playbackState.isPlaying]);

      // Helper function to check if next chapter needs generation
      const checkAndShowGenerateButton = useCallback(() => {
         if (!currentBook) return false;

         // Get the number of generated chapters (default to 0 if not provided)
         const numGeneratedChapters = currentBook.num_generated_chapters || 0;
         const totalChapters = currentBook.num_total_chapters || 1;
         const nextChapterNumber = currentChapter + 1;

         // First check if we've reached the end of the book
         if (nextChapterNumber > totalChapters) {
            console.log(`End of book reached - no more chapters to generate`);
            setShowGenerateButton(false);
            setNextChapterToGenerate(null);
            return false;
         }

         // Check if next chapter exists and has been generated
         if (
            currentChapter >= numGeneratedChapters &&
            currentChapter < totalChapters
         ) {
            console.log(
               `Next chapter (${nextChapterNumber}) needs to be generated`
            );

            // Show the generate button and set the chapter to generate
            setShowGenerateButton(true);
            setNextChapterToGenerate(nextChapterNumber);
            return true;
         }

         return false;
      }, [currentBook, currentChapter]);

      const nextChapter = useCallback(() => {
         // Check if there is a book
         if (!currentBook) return;

         console.log(
            `⚡ Next chapter button pressed for book: ${currentBook.title}`
         );

         // Get the number of generated chapters (default to 0 if not provided)
         const numGeneratedChapters = currentBook.num_generated_chapters || 0;
         const totalChapters = currentBook.num_total_chapters || 1;
         const nextChapterNumber = currentChapter + 1;

         console.log(`Current chapter: ${currentChapter}`);
         console.log(`Next chapter: ${nextChapterNumber}`);
         console.log(`Generated chapters: ${numGeneratedChapters}`);
         console.log(`Total chapters: ${totalChapters}`);

         // FIRST check if we've exceeded the total number of chapters
         if (nextChapterNumber > totalChapters) {
            console.log(
               `Cannot navigate to next chapter: Already at the last chapter.`
            );
            return;
         }

         // SECOND, check if next chapter exists and has been generated
         if (nextChapterNumber > numGeneratedChapters) {
            console.log(
               `Cannot navigate to next chapter: Chapter ${nextChapterNumber} has not been generated yet.`
            );

            // Skip to the end of the current chapter
            if (duration > 0) {
               // Set current time to duration (end of chapter)
               seekTo(duration - 0.1); // Leave 0.1 second to avoid triggering next chapter automation

               // Pause the audio if playing
               if (playbackState.isPlaying) {
                  togglePlayPause();
               }
            }

            // Show the chapter ended UI with generate button
            setIsChapterEnded(true);
            setManualChapterNavigation(true); // Set the flag indicating manual navigation
            setNextChapterToGenerate(nextChapterNumber);
            return;
         }

         // CHANGED: Instead of auto-playing, show the chapter-ended component with play button
         if (playbackState.isPlaying) {
            togglePlayPause(); // Pause current playback
         }

         // Show the chapter ended UI with the play button for the next chapter
         setIsChapterEnded(true);
         setManualChapterNavigation(true); // Set the flag indicating manual navigation
         setNextChapterToGenerate(nextChapterNumber);

         console.log(
            `Showing play button for next chapter ${nextChapterNumber}`
         );
      }, [
         currentBook,
         currentChapter,
         duration,
         playbackState.isPlaying,
         seekTo,
         togglePlayPause,
      ]);

      // Check if chapter has ended and show generate button if needed
      useEffect(() => {
         // If manual navigation is active, don't override the chapter ended state
         if (manualChapterNavigation) {
            console.log(
               "Manual chapter navigation active - not changing chapter ended state"
            );
            return;
         }

         // We consider the chapter ended if:
         // 1. We're not playing (audio has paused)
         // 2. Current time is very close to the duration (within 0.5 seconds)
         // 3. Duration is valid (greater than 0)
         const chapterEnded =
            !isPlaying &&
            duration > 0 &&
            currentTime > 0 &&
            duration - currentTime < 0.5;

         if (chapterEnded) {
            console.log(
               "Chapter has ended naturally, determining next action..."
            );

            // Get the number of generated chapters and total chapters
            const numGeneratedChapters =
               currentBook?.num_generated_chapters || 0;
            const totalChapters = currentBook?.num_total_chapters || 1;
            const nextChapterNumber = currentChapter + 1;

            console.log(`End of chapter ${currentChapter} detected`);
            console.log(
               `Book has ${numGeneratedChapters} generated chapters out of ${totalChapters} total`
            );
            console.log(`Next chapter would be ${nextChapterNumber}`);

            // FIRST check if we've reached the end of the book
            if (nextChapterNumber > totalChapters) {
               console.log(
                  `End of book reached - no more chapters to play or generate`
               );
               // Don't show chapter ended UI for the last chapter
               setIsChapterEnded(false);
               return;
            }

            // Show the chapter ended UI
            setIsChapterEnded(true);

            // Check if the immediate next chapter exists
            if (nextChapterNumber <= numGeneratedChapters) {
               console.log(
                  `Next chapter ${nextChapterNumber} exists and is ready to play`
               );
               setNextChapterToGenerate(nextChapterNumber);
            } else {
               // Next chapter needs to be generated
               console.log(
                  `Next chapter ${nextChapterNumber} needs to be generated`
               );
               if (checkAndShowGenerateButton()) {
                  setNextChapterToGenerate(nextChapterNumber);
               }
            }
         } else {
            // Only hide the chapter ended UI if not manually navigating
            setIsChapterEnded(false);
         }
      }, [
         isPlaying,
         currentTime,
         duration,
         currentChapter,
         currentBook,
         manualChapterNavigation,
         checkAndShowGenerateButton,
      ]);

      // Only load the book if it's not already loaded or if book/chapter is different
      useEffect(() => {
         const shouldLoad =
            !currentBook ||
            currentBook.uuid !== book.uuid ||
            currentChapter !== chapterNumber;

         if (shouldLoad) {
            console.log(
               "CoreAudioPlayer: Loading book in useEffect",
               book.title,
               chapterNumber
            );
            loadBook(book, chapterNumber);
            setShowGenerateButton(false);
         }
      }, [book, chapterNumber, currentBook, currentChapter, loadBook]);

      if (!loaded) {
         return (
            <div className="loading-container">
               <div className="spinner-border text" role="status">
                  <span className="visually-hidden">Loading audio...</span>
               </div>
               <div style={{ marginTop: "10px" }}>Loading audio...</div>
            </div>
         );
      }

      if (error) {
         return (
            <div className="error-container">
               Error: {error}
            </div>
         );
      }

      // Calculate chapter info
      const totalChapters =
         book?.num_total_chapters && book.num_total_chapters > 0
            ? book.num_total_chapters
            : "???";
      const chapterTitle = book?.title || "";
      const generatedChapters = book?.num_generated_chapters || 0;

      // Calculate progress percentage with safety check
      const progressPercentage =
         duration > 0 ? (currentTime / duration) * 100 : 0;
      


      return (
         <div 
            className={`audio-player-container ${shouldUseCompactMode ? 'audio-player-compact' : ''}`}
            style={containerStyle}
         >
            {/* Chapter information - only show if showChapterInfo is true and not in extreme compact mode */}
            {showChapterInfo && viewportHeight > 500 && (
               <div className={`chapter-info ${shouldUseCompactMode ? 'chapter-info-compact' : ''}`}>
                  <h2 className={`chapter-title ${shouldUseCompactMode ? 'chapter-title-compact' : ''}`}>
                     Chapter {chapterNumber} / {totalChapters}
                  </h2>
                  <p className={`book-title ${shouldUseCompactMode ? 'book-title-compact' : ''}`}>
                     {chapterTitle}
                  </p>
                  {viewportHeight > 600 && (
                     <p className="chapter-count">
                        {generatedChapters} of {totalChapters} chapters
                        generated
                     </p>
                  )}
               </div>
            )}

            {/* Simplified chapter info for very small heights */}
            {showChapterInfo && viewportHeight <= 500 && (
               <div className="mini-chapter-info">
                  <h2 className="mini-chapter-title">
                     Chapter {chapterNumber} / {totalChapters}
                  </h2>
               </div>
            )}

            {/* Autoplay helper message */}
            {showAutoplayHelper &&
               !isPlaying &&
               currentTime === 0 &&
               viewportHeight > 600 && (
                  <div className="autoplay-helper">
                     <p>
                        <strong>Ready to play!</strong> Click the play button to
                        start listening.
                     </p>
                  </div>
               )}

            {/* Use the standalone AudioPlayerNextChapter component but only if we have enough space */}
            {viewportHeight > 550 && (
               <AudioPlayerNextChapter
                  currentBook={currentBook}
                  nextChapter={nextChapterToGenerate || currentChapter + 1}
                  visible={isChapterEnded}
                  onGenerated={(generatedChapter) => {
                     // Hide the button and reset state
                     setShowGenerateButton(false);
                     setNextChapterToGenerate(null);
                     setManualChapterNavigation(false); // Reset the flag

                     // Navigate to the newly generated chapter
                     if (currentBook) {
                        loadBook(currentBook, generatedChapter, true);
                     }
                  }}
                  onPlayNext={(chapterToPlay) => {
                     // Reset the manual navigation flag
                     setManualChapterNavigation(false);

                     // Navigate to the next chapter with playback position reset
                     if (currentBook) {
                        console.log(
                           `🎯 Playing next chapter ${chapterToPlay} from beginning`
                        );

                        // First stop any current playback
                        if (isPlaying) {
                           togglePlayPause();
                        }

                        // Use loadBook but with the special ignoreSavedProgress option
                        loadBook(currentBook, chapterToPlay, true, {
                           ignoreSavedProgress: true,
                        });
                     }
                  }}
               />
            )}

            {/* Simplified next chapter button for very small heights */}
            {viewportHeight <= 550 && isChapterEnded && (
               <div style={{ textAlign: "center", marginBottom: "10px" }}>
                  <button
                     onClick={() => {
                        if (currentBook) {
                           const nextChap =
                              nextChapterToGenerate || currentChapter + 1;
                           const numGeneratedChapters =
                              currentBook.num_generated_chapters || 0;

                           if (nextChap <= numGeneratedChapters) {
                              // Play next chapter
                              loadBook(currentBook, nextChap, true, {
                                 ignoreSavedProgress: true,
                              });
                           }
                        }
                     }}
                     className="btn btn-success btn-sm"
                  >
                     Next Chapter
                  </button>
               </div>
            )}

            {/* Progress bar - always show this */}
            <div className={`progress-container ${shouldUseCompactMode ? 'progress-container-compact' : ''}`}>
               {/* Filled part of the progress bar */}
               <div
                  className="progress-bar"
                  style={{ width: `${progressPercentage}%` }}
               ></div>

               {/* Circle indicator on the progress bar */}
               <div
                  className="progress-handle"
                  style={{
                     left: `calc(${progressPercentage}% - 6px)`,
                     display: currentTime > 0 ? "block" : "none",
                  }}
               ></div>

               {/* Invisible input for seeking */}
               <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => seekTo(parseFloat(e.target.value))}
                  className="progress-slider"
               />
            </div>

            {/* Time displays */}
            <div className={`time-display ${shouldUseCompactMode ? 'time-display-compact' : ''}`}>
               {/* Current time */}
               <div className={`current-time ${shouldUseCompactMode ? 'time-compact' : ''}`}>
                  {formatTime(currentTime)}
               </div>

               {/* Only show remaining time in text format when we have enough space */}
               {viewportHeight > 600 && (
                  <div className={`time-description ${shouldUseCompactMode ? 'time-description-compact' : ''}`}>
                     {formatRemainingTime(remainingTime)}
                  </div>
               )}

               {/* Remaining time in countdown format */}
               <div className={`remaining-time ${shouldUseCompactMode ? 'time-compact' : ''}`}>
                  − {formatShortTime(remainingTime)}
               </div>
            </div>

            {/* Controls */}
            <div className={`player-controls ${shouldUseCompactMode ? 'player-controls-compact' : ''}`}>
               {/* Skip to previous chapter button */}
               <button
                  className="player-button"
                  onClick={prevChapter}
                  aria-label="Previous chapter"
               >
                  <svg
                     width={viewportHeight < 500 ? "22" : shouldUseCompactMode ? "24" : "35"}
                     height={viewportHeight < 500 ? "22" : shouldUseCompactMode ? "24" : "35"}
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M18 19L9 12L18 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                     <line
                        x1="5"
                        y1="19"
                        x2="5"
                        y2="5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
               </button>

               {/* Back 30 seconds button */}
               <button
                  className="player-button"
                  onClick={() => skip(-30)}
                  aria-label="Skip 30 seconds backward"
               >
                  <div className={`skip-button-container 
                    ${shouldUseCompactMode ? 'skip-button-container-compact' : ''} 
                    ${viewportHeight < 500 ? 'skip-button-container-mini' : ''}`}
                  >
                     <svg
                        width={viewportHeight < 500 ? "16" : shouldUseCompactMode ? "20" : "28"}
                        height={viewportHeight < 500 ? "16" : shouldUseCompactMode ? "20" : "28"}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                           marginRight: "-2px",
                           marginLeft: viewportHeight < 500 ? "-4px" : "-6px",
                        }}
                     >
                        <path
                           d="M16 18L8 12L16 6"
                           stroke="currentColor"
                           strokeWidth="2"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                     <span className={`skip-time ${shouldUseCompactMode ? 'skip-time-compact' : ''}`}
                        style={{
                           marginLeft: viewportHeight < 500 ? "-2px" : shouldUseCompactMode ? "-2px" : "-4px",
                        }}
                     >
                        30
                     </span>
                  </div>
               </button>

               {/* Play/Pause button */}
               <button
                  className={`play-button 
                    ${shouldUseCompactMode ? 'play-button-compact' : ''} 
                    ${viewportHeight < 500 ? 'play-button-mini' : ''}`}
                  onClick={togglePlayPause}
                  aria-label={isPlaying ? "Pause" : "Play"}
               >
                  {isPlaying ? (
                     <svg
                        width={viewportHeight < 500 ? "18" : shouldUseCompactMode ? "22" : "32"}
                        height={viewportHeight < 500 ? "18" : shouldUseCompactMode ? "22" : "32"}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <rect x="6" y="4" width="4" height="16" fill="white" />
                        <rect x="14" y="4" width="4" height="16" fill="white" />
                     </svg>
                  ) : (
                     <svg
                        width={viewportHeight < 500 ? "18" : shouldUseCompactMode ? "22" : "32"}
                        height={viewportHeight < 500 ? "18" : shouldUseCompactMode ? "22" : "32"}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <path d="M6 4L20 12L6 20V4Z" fill="white" />
                     </svg>
                  )}
               </button>

               {/* Forward 30 seconds button */}
               <button
                  className="player-button"
                  onClick={() => skip(30)}
                  aria-label="Skip 30 seconds forward"
               >
                  <div className={`skip-button-container 
                    ${shouldUseCompactMode ? 'skip-button-container-compact' : ''} 
                    ${viewportHeight < 500 ? 'skip-button-container-mini' : ''}`}
                  >
                     <span className={`skip-time ${shouldUseCompactMode ? 'skip-time-compact' : ''}`}
                        style={{
                           marginRight: viewportHeight < 500 ? "-2px" : shouldUseCompactMode ? "-2px" : "-4px",
                        }}
                     >
                        30
                     </span>
                     <svg
                        width={viewportHeight < 500 ? "16" : shouldUseCompactMode ? "20" : "28"}
                        height={viewportHeight < 500 ? "16" : shouldUseCompactMode ? "20" : "28"}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginLeft: "-2px" }}
                     >
                        <path
                           d="M8 6L16 12L8 18"
                           stroke="currentColor"
                           strokeWidth="2"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                  </div>
               </button>

               {/* Skip to next chapter button */}
               <button
                  className="player-button"
                  onClick={nextChapter}
                  aria-label="Next chapter"
               >
                  <svg
                     width={viewportHeight < 500 ? "22" : shouldUseCompactMode ? "24" : "35"}
                     height={viewportHeight < 500 ? "22" : shouldUseCompactMode ? "24" : "35"}
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M6 5L15 12L6 19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                     <line
                        x1="19"
                        y1="5"
                        x2="19"
                        y2="19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
               </button>
            </div>

            {/* Close button (if onClose function is provided) - only show when we have enough space */}
            {onClose && viewportHeight > 700 && (
               <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <button
                     onClick={onClose}
                     className="close-button"
                  >
                     Close Player
                  </button>
               </div>
            )}
         </div>
      );
   }
);

// Updated GlobalAudioPlayer - Minibar for the bottom of the screen
export const GlobalAudioPlayer = ({ currentView }) => {
   const [isMinimized, setIsMinimized] = useState(true);
   const [coverImageUrl, setCoverImageUrl] = useState(null);
   const [coverLoading, setCoverLoading] = useState(false);
   const [coverError, setCoverError] = useState(false);

   // Add viewport height tracking
   const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

   useEffect(() => {
      const handleResize = () => {
         setViewportHeight(window.innerHeight);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
   }, []);

   const { currentBook, currentChapter, playbackState, togglePlayPause } =
      useContext(AudioContext);

   const { isPlaying, currentTime } = playbackState;
   const { userId } = useUser();
   const { theme } = useTheme(); // Get current theme

   // Calculate if we should use compact mode
   const shouldUseCompactMode = viewportHeight < 700;

   // Create a timestamp display for the minimized player
   const getTimeDisplay = () => {
      if (!currentBook) return "";

      const minutes = Math.floor(currentTime / 60);
      const seconds = Math.floor(currentTime % 60);
      return `${minutes.toString().padStart(2, "0")}:${seconds
         .toString()
         .padStart(2, "0")}`;
   };

   // Function to toggle between minimized and expanded states
   const toggleMinimize = () => {
      setIsMinimized(!isMinimized);
   };

   // Load cover image when book changes
   useEffect(() => {
      // Skip loading if we're on the generation page or have no book
      if (currentView === "generate" || !currentBook) {
         setCoverImageUrl(null);
         return;
      }

      // Static cache for cover images
      if (!window.audioCoverCache) {
         window.audioCoverCache = {};
      }

      const loadCoverImage = async () => {
         // First check if we already have this cover in our cache
         const cacheKey = `cover-${currentBook.uuid}`;
         if (window.audioCoverCache[cacheKey]) {
            console.log("Using cached cover image");
            setCoverImageUrl(window.audioCoverCache[cacheKey]);
            return;
         }

         setCoverLoading(true);
         setCoverError(false);

         try {
            // For non-public books, ensure we have a CDS token
            let cdsToken = null;
            if (!currentBook.is_public) {
               try {
                  cdsToken = await getCurrentCDSToken();
               } catch (err) {
                  console.error("Failed to get CDS token:", err);
                  throw new Error("Failed to authenticate with CDS");
               }
            }

            // Include user_id in the URL for the CDS
            const imageUrl = `${config.cdsUrl}/user/audiobooks/${currentBook.uuid}/cover?size=256x256&user_id=${userId}`;

            const headers = {
               Accept: "image/jpeg,image/png,*/*",
               Origin: "${config.primaryUrl}",
            };

            if (cdsToken) {
               headers["Authorization"] = JSON.stringify(cdsToken);
            }

            const response = await fetch(imageUrl, {
               method: "GET",
               headers,
               credentials: "include",
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load image: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            // Store in our cache
            window.audioCoverCache[cacheKey] = objectUrl;

            setCoverImageUrl(objectUrl);
            setCoverLoading(false);
         } catch (err) {
            console.error("Error loading cover image:", err);
            setCoverError(true);
            setCoverLoading(false);
         }
      };

      loadCoverImage();

      // Clean up function to revoke object URLs when component unmounts
      return () => {
         // We don't revoke cached URLs here as they might be used again
         // They'll be cleaned up when the app is closed
      };
   }, [currentBook, userId, currentView]);

   // Early return if we're on the generation page
   if (currentView === "generate") {
      return null; // Don't render anything on the generation page
   }

   // Calculate position based on viewport size
   const getPlayerPosition = () => {
      // For very small screens, position at the bottom
      if (viewportHeight < 500) {
         return {
            bottom: "0",
            right: "0",
            left: "0",
            width: "100%",
            borderRadius: "0",
         };
      }

      // Normal position
      return {
         bottom: "20px",
         right: "20px",
         width: isMinimized ? "250px" : "450px",
      };
   };

   const playerPosition = getPlayerPosition();

   return (
      <div
         className={`global-audio-player ${isMinimized ? 'player-minimized' : 'player-expanded'}`}
         style={playerPosition}
      >
         {isMinimized ? (
            <div className="minimized-content">
               {/* Play/Pause Button - doesn't expand player when clicked */}
               <div
                  onClick={(e) => {
                     e.stopPropagation();
                     if (currentBook) togglePlayPause();
                  }}
                  className={`play-pause-button ${
                     currentBook 
                        ? isPlaying 
                           ? 'play-pause-button-playing' 
                           : 'play-pause-button-paused'
                        : 'play-pause-button-inactive'
                  }`}
               >
                  {currentBook && isPlaying ? (
                     <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <rect x="6" y="4" width="4" height="16" fill="white" />
                        <rect x="14" y="4" width="4" height="16" fill="white" />
                     </svg>
                  ) : (
                     <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <path d="M6 4L20 12L6 20V4Z" fill="white" />
                     </svg>
                  )}
               </div>

               {/* Book Title and Time - this area expands the player when clicked */}
               <div
                  onClick={toggleMinimize}
                  className="minimized-info"
               >
                  {currentBook ? (
                     <>
                        <div className="minimized-title">
                           {currentBook.title || "Unknown Title"}
                        </div>
                        <div className="minimized-details">
                           <span className="minimized-time">
                              {getTimeDisplay()}
                           </span>
                           <span className="minimized-chapter">
                              Chapter {currentChapter}
                           </span>
                        </div>
                     </>
                  ) : (
                     <div className="minimized-message">
                        No audiobook playing
                     </div>
                  )}
               </div>

               {/* Expand indicator */}
               <div className="expand-indicator">
                  <svg
                     width="14"
                     height="14"
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
               </div>
            </div>
         ) : (
            <div>
               <div
                  onClick={toggleMinimize}
                  className="expanded-header"
               >
                  <h4 className="expanded-title">
                     {currentBook ? currentBook.title : "Audio Player"}
                  </h4>
                  <button
                     onClick={(e) => {
                        e.stopPropagation(); // Prevent bubbling to parent onClick
                        toggleMinimize();
                     }}
                     className="minimize-button"
                  >
                     <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <path
                           d="M5 12H19"
                           stroke="currentColor"
                           strokeWidth="2.5"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                  </button>
               </div>

               {currentBook ? (
                  <div style={{ maxWidth: "100%", padding: "0 10px" }}>
                     {/* Book cover image - only show if we have enough space */}
                     {viewportHeight > 550 && (
                        <div className="cover-container">
                           <div className={`cover-image ${shouldUseCompactMode ? 'cover-image-compact' : ''}`}>
                              {coverLoading ? (
                                 <div className="cover-placeholder">
                                    <span>Loading...</span>
                                 </div>
                              ) : coverError ? (
                                 <div className="cover-placeholder">
                                    <span style={{ color: "var(--accent-danger)" }}>Error</span>
                                 </div>
                              ) : coverImageUrl ? (
                                 <img
                                    src={coverImageUrl}
                                    alt={`${currentBook.title || "Book"} cover`}
                                    style={{
                                       width: "100%",
                                       height: "100%",
                                       objectFit: "cover",
                                    }}
                                 />
                              ) : (
                                 <div className="cover-placeholder">
                                    <span>No Cover</span>
                                 </div>
                              )}
                           </div>
                        </div>
                     )}

                     {/* Use CoreAudioPlayer with compact mode for small viewports */}
                     <CoreAudioPlayer
                        book={currentBook}
                        chapterNumber={currentChapter}
                        showChapterInfo={viewportHeight > 500} // Only show chapter info if we have space
                        showAutoplayHelper={false}
                        compact={shouldUseCompactMode}
                        containerStyle={{
                           padding: "0 0 15px 0",
                           backgroundColor: "transparent", // Override the default background
                           boxShadow: "none", // Remove the shadow
                           borderRadius: 0, // Remove the border radius
                        }}
                     />
                  </div>
               ) : (
                  <div className={`no-audiobook-message ${shouldUseCompactMode ? 'no-audiobook-message-compact' : ''}`}>
                     <p>No audiobook currently playing.</p>
                     {viewportHeight > 600 && (
                        <p style={{ fontSize: "14px", marginTop: "10px" }}>
                           Visit the Generate Audiobook page to create and play
                           audiobooks.
                        </p>
                     )}
                  </div>
               )}
            </div>
         )}
      </div>
   );
};
// ===== PLAYER UI COMPONENTS =====

export const IntegratedAudioPlayer = memo(({ book, chapterNumber }) => {
   // Simply use CoreAudioPlayer with default settings for integrated view
   return (
      <CoreAudioPlayer
         book={book}
         chapterNumber={chapterNumber}
         showChapterInfo={true}
         showAutoplayHelper={true}
         compact={false}
      />
   );
});
